import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { ME_LINK, NAVIGATION } from "@/app/navigation";
import { Brand } from "@/components/brand";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { TenantSwitcher } from "./TenantSwitcher";
import { UserMenu } from "./UserMenu";

export function Sidebar({
  collapsed = false,
  onToggle,
  onNavigate,
  badges = {},
}: {
  collapsed?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  badges?: Partial<Record<"unassigned", number>>;
}) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();

  const item = (to: string, label: string, Icon: React.ComponentType<{ className?: string }>, badge?: number, end = false) => {
    const link = (
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "relative flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            isActive && "bg-sidebar-accent font-medium text-sidebar-accent-foreground before:absolute before:top-2 before:bottom-2 before:-left-3 before:w-[3px] before:rounded-full before:bg-sidebar-primary",
            collapsed && "justify-center px-0",
          )
        }
      >
        <Icon className="size-4 shrink-0" />
        {!collapsed && <span className="flex-1 truncate">{label}</span>}
        {!!badge && (
          <span
            className={cn(
              "tabular rounded-full bg-primary px-1.5 text-[11px] leading-[18px] font-medium text-primary-foreground",
              collapsed && "absolute top-1 right-1 size-2 p-0 text-[0px]",
            )}
          >
            {badge}
          </span>
        )}
      </NavLink>
    );
    if (!collapsed) return link;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="flex h-full flex-col gap-4 bg-sidebar px-3 py-4">
      <div className={cn("flex items-center justify-between px-1", collapsed && "justify-center")}>
        <Brand collapsed={collapsed} />
        {onToggle && !collapsed && (
          <button onClick={onToggle} className="rounded-md p-1 text-muted-foreground hover:bg-sidebar-accent" aria-label="Seitenleiste einklappen">
            <PanelLeftClose className="size-4" />
          </button>
        )}
      </div>
      <TenantSwitcher collapsed={collapsed} />
      <nav className="flex flex-1 flex-col gap-5 overflow-y-auto">
        {NAVIGATION.map((section) => {
          const items = section.items.filter((i) => hasRole(...i.roles));
          if (items.length === 0) return null;
          return (
            <div key={section.labelKey} className="flex flex-col gap-0.5">
              {!collapsed && (
                <p className="px-2.5 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">{t(section.labelKey)}</p>
              )}
              {items.map((i) => (
                <div key={i.to}>{item(i.to, t(i.labelKey), i.icon, i.badge ? badges[i.badge] : undefined, i.to === "/app")}</div>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="flex flex-col gap-1 border-t pt-3">
        {item(ME_LINK.to, t(ME_LINK.labelKey), ME_LINK.icon)}
        {onToggle && collapsed && (
          <button onClick={onToggle} className="flex h-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-sidebar-accent" aria-label="Seitenleiste ausklappen">
            <PanelLeftOpen className="size-4" />
          </button>
        )}
        <UserMenu collapsed={collapsed} />
      </div>
    </div>
  );
}
